import {
  AccessTypeEnum,
  OpeningEntityTypeEnum,
  OpeningHourDefinitionsInDTO,
  OpeningModeEnum,
  SaveTmplV2InDTO,
  TemplateDistributionTypeEnum,
  TemplateKindEnum,
  TmplV2HeaderDTO,
  TmplV2StopInDTO,
} from '@api/ringil-api.v2.tsx';
import { RoleEnum } from '@type/enums.ts';
import { generateUUID } from '@utils/utils.tsx';
import { applyExpeditionDefaults } from '@app/pages/expedition/create/defaults.ts';
import { LocalityEntryModeEnum } from '@app/pages/expedition/create/types.ts';
import { toContractedCarrierInDTO, toPriceTableDTO } from '@components/ringil3/Features/Contracted/types.ts';
import { newPriceTable } from '@utils/contractedPriceTable.ts';
import { LineForm, LineStop } from '@app/pages/customer/LineEdit/types.ts';


export const DEFAULT_LINE_INTERVAL_HOURS = 24;

const emptyStop = (isLast: boolean): LineStop =>
  ({
    id: generateUUID(),
    localityMode: LocalityEntryModeEnum.FROM_LOCATIONS,
    location: null,
    manualAddress: null,
    loadingReference: null,
    orders: isLast ? null : [],
    unloadingOrderIds: isLast ? [] : null,
    timeslots: { dayWithInterval: [] },
    notifySelectedPerson: false,
    candidates: [],
  }) as LineStop;

/** A blank Linka: two stops, the shipment defaults (cargo type), a one-day demand validity and a one-level price table. */
export const newLineInitialData = (name: string, currency: string, levelPrefix: string): LineForm =>
  applyExpeditionDefaults({
    name,
    bothDirections: false,
    priceTable: newPriceTable(currency, levelPrefix),
    locations: [emptyStop(false), emptyStop(true)],
    distribution: null,
    contractedCarriers: [],
    capacity: { ladenFactor: null, cargoTemplateCode: null, cargoTemplateName: null },
    allowPartialDelivery: false,
    intervalHours: DEFAULT_LINE_INTERVAL_HOURS,
    existingAttachmentIds: [],
    attachments: [],
    labels: [],
    notifiedTargets: [],
  } as LineForm);

/** True when at least one day has a window; an all-closed week is "no pattern" and is not stored. */
export const hasOpeningWindow = (openingHours: OpeningHourDefinitionsInDTO | undefined): boolean =>
  !!openingHours?.openingHoursDays?.some(day => day.openingMode !== OpeningModeEnum.CLOSED);

const mapStop = (stop: LineStop): TmplV2StopInDTO => ({
  id: stop.stopId,
  candidateLocationIds: Array.from(new Set((stop.candidates ?? []).map(c => c.locationId).filter(id => !!id))),
  loadingReference: stop.loadingReference || undefined,
  locationNote: stop.stopLocationNote || undefined,
  orders: stop.orders ?? [],
  unloadingOrderIds: stop.unloadingOrderIds ?? [],
  requirements: stop.requirements ?? [],
  openingHours: hasOpeningWindow(stop.openingHours)
    ? {
        ...stop.openingHours,
        entityId: stop.stopId ?? undefined,
        openingEntityType: OpeningEntityTypeEnum.TMPL_V2_STOP,
      }
    : undefined,
});

export const lineToSaveRequest = (form: LineForm): SaveTmplV2InDTO => {
  const existing = new Set(form.existingAttachmentIds ?? []);
  const current = new Set((form.attachments ?? []).map(a => a.attachmentId));
  const ladenFactor = form.capacity?.ladenFactor || null;

  return {
    templateKind: TemplateKindEnum.LINE,
    name: form.name?.trim(),
    header: {
      transportationType: form.transportationType,
      expeditionType: form.expeditionType,
      truckTypeCode: form.truckTypeCode,
      loadingTypes: form.loadingTypes ?? [],
      cargoType: form.cargoType as TmplV2HeaderDTO['cargoType'],
      requirements: form.specialRequirements ?? [],
      carrierNote: form.carrierNote,
    },
    capacity: ladenFactor ? { ladenFactor, cargoTemplateCode: form.capacity?.cargoTemplateCode || undefined } : undefined,
    allowPartialDelivery: ladenFactor ? !!form.allowPartialDelivery : false,
    bothDirections: !!form.bothDirections,
    stops: form.locations.map(mapStop),
    attachments: (form.attachments ?? [])
      .filter(a => !existing.has(a.attachmentId))
      .map(a => ({
        attachmentId: a.attachmentId,
        documentType: a.documentType,
        userNote: a.userNote,
        userDateLocalDate: a.userDateLocalDate ?? null,
        accessType: a.accessType ? a.accessType : AccessTypeEnum.ALL,
        labelIds: a.labels?.map(l => l.labelId) ?? [],
      })),
    removedAttachmentIds: [...existing].filter(id => !current.has(id)),
    distribution: {
      templateDistributionType: TemplateDistributionTypeEnum.CONTRACTED,
      intervalHours: form.intervalHours ?? undefined,
      contractedCarriers: (form.contractedCarriers ?? []).map(c => toContractedCarrierInDTO(c, form.priceTable)),
      // the backend takes MU and default laden factor from the capacity
      priceTable: toPriceTableDTO(form.priceTable),
    },
    internalInfo: {
      responsiblePersonId: form.person?.id,
      notifiedPersonIds: [],
      notifiedTargets: form.notifiedTargets ?? [],
      internalNote: form.internalNote,
      disponentPersonId: form.disponentPerson?.id,
      invoiceState: form.invoiceState,
      pairingSymbol: form.pairingSymbol,
      rebilling: form.rebilling
        ? {
            invoiceRebillingType: form.rebilling.invoiceRebillingType,
            invoiceDepartment: form.rebilling.invoiceDepartment,
            invoicePartnerId: form.rebilling.invoicePartner?.companyId,
          }
        : undefined,
      notifyByRole: form.notifyByRole ? RoleEnum.ROLE_EXP_NOTIFY : undefined,
      folderIds: [...(form.folders?.map(f => f.id) ?? []), ...(form.hiddenFolderIds ?? [])],
      labelIds: [...(form.labels?.map(l => l.labelId) ?? []), ...(form.hiddenLabelIds ?? [])],
      internalInfoAccountCodeListId: form.internalInfoAccountCodeListId,
      internalInfoCostCenterCodeListId: form.internalInfoCostCenterCodeListId,
      internalInfoCostUnitCodeListId: form.internalInfoCostUnitCodeListId,
      internalInfoProjectCodeListId: form.internalInfoProjectCodeListId,
    },
  };
};
